import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { X, ShieldCheck, Warehouse, Repeat, CheckCircle2, ChevronDown } from "lucide-react";
import { AuthService } from "../../../services/auth.service";
import { warehouseService } from "../../../services/warehouse.service";
import {
    getAuthSession,
    getAuthUser,
    setAuthSession,
} from "../../AdminAuth/components/authStore";

export default function DashboardSwitchModal({ open, onClose, onSwitched }) {
    const session = getAuthSession() || {};
    const user = getAuthUser() || {};

    const dashboardAccess = session?.dashboardAccess || {};
    const warehouseAccess = session?.warehouseAccess || {};

    const isSuperAdmin = String(user?.role || "").toLowerCase() === "super_admin";

    const canOpenAdminDashboard = !!dashboardAccess?.canOpenAdminDashboard;
    const canOpenWarehouseDashboard = !!dashboardAccess?.canOpenWarehouseDashboard;

    const rawWarehouseIds = Array.isArray(warehouseAccess?.warehouseIds)
        ? warehouseAccess.warehouseIds
        : [];

    const accessibleWarehouseIds = [
        ...new Set(
            [
                ...rawWarehouseIds,
                warehouseAccess?.assignedWarehouseId,
                session?.selectedWarehouseId,
            ].filter(Boolean)
        ),
    ];

    const currentDashboard = session?.currentDashboard || "admin";
    const assignedWarehouseId = warehouseAccess?.assignedWarehouseId || "";
    const storedSelectedWarehouseId = session?.selectedWarehouseId || "";

    const [switching, setSwitching] = useState(false);
    const [loadingWarehouses, setLoadingWarehouses] = useState(false);
    const [warehouseOptions, setWarehouseOptions] = useState([]);
    const [selectedWarehouseId, setSelectedWarehouseId] = useState(
        storedSelectedWarehouseId || assignedWarehouseId || ""
    );

    const [warehouseDropdownOpen, setWarehouseDropdownOpen] = useState(false);
    useEffect(() => {
        if (!open) return;

        let ignore = false;

        async function loadWarehouses() {
            try {
                if (!canOpenWarehouseDashboard) {
                    if (!ignore) setWarehouseOptions([]);
                    return;
                }

                setLoadingWarehouses(true);

                const res = await warehouseService.listWarehouses({
                    page: 1,
                    limit: 200,
                });

                const rows =
                    res?.items ||
                    res?.data?.items ||
                    res?.rows ||
                    res?.data?.rows ||
                    res?.warehouses ||
                    [];

                const allWarehouses = rows
                    .filter(Boolean)
                    .map((w) => ({
                        id: w.id,
                        name:
                            w.name ||
                            w.warehouseName ||
                            w.code ||
                            w.locationName ||
                            w.slug ||
                            w.id,
                    }))
                    .filter((w) => w.id);

                if (isSuperAdmin || warehouseAccess?.mode === "all") {
                    if (!ignore) {
                        setWarehouseOptions(allWarehouses);
                        setSelectedWarehouseId(
                            (prev) =>
                                prev ||
                                storedSelectedWarehouseId ||
                                assignedWarehouseId ||
                                allWarehouses?.[0]?.id ||
                                ""
                        );
                    }
                    return;
                }

                const mapped = allWarehouses.filter((w) =>
                    accessibleWarehouseIds.includes(w.id)
                );

                const fallbackMissing = accessibleWarehouseIds
                    .filter((id) => !mapped.some((w) => w.id === id))
                    .map((id) => ({ id, name: id }));

                const finalRows = [...mapped, ...fallbackMissing];

                if (!ignore) {
                    setWarehouseOptions(finalRows);
                    setSelectedWarehouseId(
                        (prev) =>
                            prev ||
                            storedSelectedWarehouseId ||
                            assignedWarehouseId ||
                            finalRows?.[0]?.id ||
                            ""
                    );
                }
            } catch (err) {
                console.error("Failed to load warehouses", err);
                if (!ignore) setWarehouseOptions([]);
            } finally {
                if (!ignore) setLoadingWarehouses(false);
            }
        }

        loadWarehouses();

        return () => {
            ignore = true;
        };
    }, [
        open,
        canOpenWarehouseDashboard,
        isSuperAdmin,
        warehouseAccess?.mode,
        storedSelectedWarehouseId,
        assignedWarehouseId,
        JSON.stringify(accessibleWarehouseIds),
    ]);

    useEffect(() => {
        if (!open) return;

        const onKey = (e) => {
            if (e.key === "Escape") onClose?.();
        };

        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, onClose]);

    const switchToAdmin = async () => {
        try {
            if (switching || !canOpenAdminDashboard) return;

            setSwitching(true);

            const res = await AuthService.switchContext({
                dashboard: "admin",
            });

            setAuthSession({
                accessToken: res?.accessToken,
                admin: res?.admin,
                dashboardAccess: res?.dashboardAccess,
                warehouseAccess: res?.warehouseAccess,
                currentDashboard: res?.currentDashboard,
                selectedWarehouseId: res?.selectedWarehouseId,
            });

            onSwitched?.("admin");
            onClose?.();
        } catch (err) {
            alert(err?.message || "Failed to switch dashboard");
        } finally {
            setSwitching(false);
        }
    };

    const switchToWarehouse = async () => {
        try {
            if (switching || !canOpenWarehouseDashboard) return;

            const finalWarehouseId =
                selectedWarehouseId ||
                assignedWarehouseId ||
                warehouseOptions?.[0]?.id ||
                null;

            if (warehouseOptions.length > 0 && !finalWarehouseId) {
                alert("Please select warehouse first");
                return;
            }

            setSwitching(true);

            const res = await AuthService.switchContext({
                dashboard: "warehouse",
                warehouseId: finalWarehouseId,
            });

            setAuthSession({
                accessToken: res?.accessToken,
                admin: res?.admin,
                dashboardAccess: res?.dashboardAccess,
                warehouseAccess: res?.warehouseAccess,
                currentDashboard: res?.currentDashboard,
                selectedWarehouseId: res?.selectedWarehouseId,
            });

            onSwitched?.("warehouse");
            onClose?.();
        } catch (err) {
            alert(err?.message || "Failed to switch dashboard");
        } finally {
            setSwitching(false);
        }
    };

    const selectedWarehouseValue = assignedWarehouseId || selectedWarehouseId;

    const selectedWarehouseLabel =
        warehouseOptions.find((w) => w.id === selectedWarehouseValue)?.name ||
        (loadingWarehouses ? "Loading warehouses..." : "Select warehouse");

    const warehouseDisabled = useMemo(() => {
        if (!canOpenWarehouseDashboard) return true;
        if (loadingWarehouses) return true;
        if (warehouseOptions.length > 0 && !selectedWarehouseId && !assignedWarehouseId) {
            return true;
        }
        return false;
    }, [
        canOpenWarehouseDashboard,
        loadingWarehouses,
        warehouseOptions.length,
        selectedWarehouseId,
        assignedWarehouseId,
    ]);

    if (!open || typeof document === "undefined") return null;

    return createPortal(
        <div className="fixed inset-0 z-[9999] overflow-y-auto">
            <button
                type="button"
                className="absolute inset-0 bg-[rgba(15,23,42,0.22)] backdrop-blur-[3px]"
                onClick={onClose}
                aria-label="Close switch dashboard modal"
            />

            <div className="relative z-[1] flex min-h-full items-center justify-center p-4">
                <div className="relative w-full max-w-[760px] rounded-[28px] border border-[#f2c6cc] bg-white shadow-[0_30px_90px_rgba(15,23,42,0.18)]">
                    <button
                        type="button"
                        onClick={onClose}
                        className="absolute right-5 top-5 flex h-10 w-10 items-center justify-center rounded-full border border-[#f2c6cc] bg-white text-slate-500 transition hover:bg-[#fff7f8] hover:text-[#d86a78]"
                    >
                        <X size={18} />
                    </button>

                    <div className="border-b border-[#f7dbe0] px-7 py-6">
                        <div className="flex items-center gap-3">
                            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#fff3f5] text-[#d86a78]">
                                <Repeat size={22} />
                            </div>

                            <div>
                                <h2 className="text-[24px] font-bold text-slate-800">
                                    Switch Panel
                                </h2>
                                <p className="mt-1 text-[13px] text-slate-500">
                                    Choose which workspace you want to open.
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="grid gap-5 p-7 md:grid-cols-2">
                        <div className="rounded-[22px] border border-[#f2c6cc] bg-[#fff9fa] p-5">
                            <div className="flex items-start justify-between gap-3">
                                <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white text-[#d86a78] shadow-sm">
                                    <ShieldCheck size={22} />
                                </div>

                                {currentDashboard === "admin" ? (
                                    <span className="inline-flex items-center gap-1 rounded-full border border-[#cfead7] bg-[#effaf2] px-3 py-1 text-[11px] font-semibold text-[#2f8f4f]">
                                        <CheckCircle2 size={13} />
                                        Active
                                    </span>
                                ) : null}
                            </div>

                            <h3 className="mt-4 text-[20px] font-bold text-slate-800">
                                Admin Panel
                            </h3>

                            <p className="mt-2 text-[13px] leading-6 text-slate-500">
                                Manage employees, roles, products, permissions and overall admin operations.
                            </p>

                            <button
                                type="button"
                                onClick={switchToAdmin}
                                disabled={!canOpenAdminDashboard || switching || currentDashboard === "admin"}
                                className="mt-6 h-[46px] w-full rounded-[14px] border border-[#f2c6cc] bg-white text-[14px] font-semibold text-slate-700 transition hover:bg-[#fff3f5] disabled:cursor-not-allowed disabled:opacity-55"
                            >
                                {currentDashboard === "admin" ? "Admin Active" : "Open Admin Panel"}
                            </button>
                        </div>

                        <div className="rounded-[22px] border border-[#f2c6cc] bg-[#fff9fa] p-5">
                            <div className="flex items-start justify-between gap-3">
                                <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white text-[#d86a78] shadow-sm">
                                    <Warehouse size={22} />
                                </div>

                                {currentDashboard === "warehouse" ? (
                                    <span className="inline-flex items-center gap-1 rounded-full border border-[#cfead7] bg-[#effaf2] px-3 py-1 text-[11px] font-semibold text-[#2f8f4f]">
                                        <CheckCircle2 size={13} />
                                        Active
                                    </span>
                                ) : null}
                            </div>

                            <h3 className="mt-4 text-[20px] font-bold text-slate-800">
                                Warehouse Panel
                            </h3>

                            <p className="mt-2 text-[13px] leading-6 text-slate-500">
                                Manage inventory, stock movement, warehouse staff and warehouse operations.
                            </p>

                            {canOpenWarehouseDashboard ? (
                                <div className="mt-5">
                                    <label className="mb-2 block text-[12px] font-semibold text-slate-600">
                                        Select Warehouse
                                    </label>

                                    <div
                                        className="relative"
                                        onBlur={(e) => {
                                            if (!e.currentTarget.contains(e.relatedTarget)) {
                                                setWarehouseDropdownOpen(false);
                                            }
                                        }}
                                    >
                                        <button
                                            type="button"
                                            disabled={switching || loadingWarehouses || warehouseAccess?.mode === "assigned"}
                                            onClick={() => setWarehouseDropdownOpen((v) => !v)}
                                            className="flex h-[46px] w-full items-center justify-between gap-3 rounded-[14px] border border-[#f2c6cc] bg-white px-4 text-left text-[13px] text-slate-700 outline-none transition hover:border-[#df8b96] focus:border-[#d86a78] focus:ring-4 focus:ring-[#fdecef] disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-400"
                                        >
                                            <span className="min-w-0 flex-1 truncate">
                                                {selectedWarehouseLabel}
                                            </span>

                                            <ChevronDown
                                                size={17}
                                                className={`shrink-0 text-slate-400 transition ${warehouseDropdownOpen ? "rotate-180 text-[#d86a78]" : ""
                                                    }`}
                                            />
                                        </button>

                                        {warehouseDropdownOpen && !loadingWarehouses ? (
                                            <div className="absolute left-0 right-0 top-[52px] z-[10001] overflow-hidden rounded-[14px] border border-[#f2c6cc] bg-white shadow-[0_18px_45px_rgba(15,23,42,0.16)]">
                                                <div className="max-h-[190px] overflow-y-auto py-1 [scrollbar-width:thin] [scrollbar-color:#d86a78_#fff1f3] [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-[#d86a78] [&::-webkit-scrollbar-track]:bg-[#fff1f3]">
                                                    {warehouseOptions.length === 0 ? (
                                                        <div className="px-4 py-3 text-[13px] text-slate-400">
                                                            No warehouse found
                                                        </div>
                                                    ) : (
                                                        warehouseOptions.map((w) => {
                                                            const active = w.id === selectedWarehouseValue;

                                                            return (
                                                                <button
                                                                    key={w.id}
                                                                    type="button"
                                                                    onMouseDown={(e) => e.preventDefault()}
                                                                    onClick={() => {
                                                                        setSelectedWarehouseId(w.id);
                                                                        setWarehouseDropdownOpen(false);
                                                                    }}
                                                                    className={`flex w-full items-center justify-between gap-3 px-4 py-2.5 text-left text-[13px] transition ${active
                                                                            ? "bg-[#fff1f3] font-semibold text-[#c95b69]"
                                                                            : "text-slate-700 hover:bg-[#fff7f8]"
                                                                        }`}
                                                                >
                                                                    <span className="min-w-0 flex-1 truncate">
                                                                        {w.name}
                                                                    </span>

                                                                    {active ? (
                                                                        <CheckCircle2 size={15} className="shrink-0 text-[#d86a78]" />
                                                                    ) : null}
                                                                </button>
                                                            );
                                                        })
                                                    )}
                                                </div>
                                            </div>
                                        ) : null}
                                    </div>
                                </div>
                            ) : null}

                            <button
                                type="button"
                                onClick={switchToWarehouse}
                                disabled={warehouseDisabled || switching}
                                className="mt-6 h-[46px] w-full rounded-[14px] bg-[#d86a78] text-[14px] font-semibold text-white transition hover:bg-[#c95b69] disabled:cursor-not-allowed disabled:opacity-55"
                            >
                                {switching
                                    ? "Opening..."
                                    : currentDashboard === "warehouse"
                                        ? "Switch Warehouse"
                                        : "Open Warehouse Panel"}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
}